/**
 * Cart Drawer Initialization Manager
 *
 * Handles reinitialization of all interactive components in the cart drawer
 * after Shopify's section rendering API replaces the HTML.
 */

class CartDrawerInitManager {
  constructor() {
    this.initialized = false;
    this.initCallbacks = [];
    this.isReinitializing = false;
    this.pendingReinit = false;
    // Don't set up observer or subscribe here - we'll do it once explicitly
  }

  /**
   * Register a callback to be executed when cart drawer initializes
   */
  registerInitCallback(callback, name = 'anonymous') {
    this.initCallbacks.push({ callback, name });
    console.log(`[CartDrawerInit] Registered callback: ${name}`);
  }

  /**
   * Setup MutationObserver to watch for cart drawer updates
   * NOTE: This is NOT used for triggering reinits, only for monitoring
   */
  setupObserver() {
    const targetNode = document.querySelector('cart-drawer');
    if (!targetNode) {
      console.warn('[CartDrawerInit] cart-drawer element not found');
      return;
    }

    // Observer to watch for changes in the cart drawer
    // REMOVED: scheduleReinit() call - DOM updates trigger reinit via cart-items events only
    const observer = new MutationObserver((mutations) => {
      // Just log for debugging, don't trigger reinit
      const hasContentUpdate = mutations.some(mutation => {
        return mutation.type === 'childList' &&
               mutation.target.matches('.drawer__inner, #CartDrawer, cart-drawer-items');
      });

      if (hasContentUpdate) {
        console.log('[CartDrawerInit] DOM content changed (monitored only, not triggering reinit)');
      }
    });

    observer.observe(targetNode, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Subscribe to Shopify cart update events
   * REMOVED: scheduleReinit() calls - let cart.js handle this centrally
   */
  subscribeToCartUpdates() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        console.log('[CartDrawerInit] Cart update event received, source:', event.source);
        // Always animate new items after cart updates
        // Small delay to ensure DOM has updated
        setTimeout(() => {
          this.animateNewCartItems();
        }, 100);
      });
      console.log('[CartDrawerInit] Subscribed to cart updates (animation only)');
    }
  }

  /**
   * Track existing cart items to detect new ones
   */
  previousCartItemIds = new Set();

  /**
   * Initialize cart item tracking
   */
  initializeCartItemTracking() {
    const cartItems = document.querySelectorAll('.cart-drawer .cart-item');
    cartItems.forEach((item) => {
      const id = item.id || item.getAttribute('data-cart-item-key');
      if (id) {
        this.previousCartItemIds.add(id);
      }
    });
  }

  /**
   * Add animation to new cart items
   */
  animateNewCartItems() {
    // Wait for section rendering to complete
    requestAnimationFrame(() => {
      setTimeout(() => {
        const cartItems = document.querySelectorAll('.cart-drawer .cart-item');
        const currentItemIds = new Set();
        
        // Collect current item IDs
        cartItems.forEach((item) => {
          const id = item.id || item.getAttribute('data-cart-item-key');
          if (id) {
            currentItemIds.add(id);
          }
        });

        // Find new items (items that weren't in previous set)
        const newItems = Array.from(cartItems).filter((item) => {
          const id = item.id || item.getAttribute('data-cart-item-key');
          return id && !this.previousCartItemIds.has(id);
        });

        // If no previous items tracked, animate all items (initial load or full refresh)
        if (this.previousCartItemIds.size === 0) {
          cartItems.forEach((item, index) => {
            item.classList.add('animate-in');
            setTimeout(() => {
              item.classList.remove('animate-in');
            }, 600);
          });
        } else if (newItems.length > 0) {
          // Animate only new items
          newItems.forEach((item, index) => {
            item.classList.add('animate-in');
            // Stagger animations slightly
            setTimeout(() => {
              item.classList.remove('animate-in');
            }, 600);
          });
        }

        // Update tracked IDs
        this.previousCartItemIds = currentItemIds;
      }, 50);
    });
  }

  /**
   * Add animation to the newest cart item (legacy method for compatibility)
   */
  animateNewCartItem() {
    this.animateNewCartItems();
  }

  /**
   * Schedule reinitialization after DOM updates
   * CRITICAL: Only call this from cart.js after DOM replacement is complete
   */
  scheduleReinit() {
    // If already reinitializing, mark that we need another pass
    if (this.isReinitializing) {
      console.log('[CartDrawerInit] Reinit already in progress, marking pending');
      this.pendingReinit = true;
      return;
    }

    // Debounce to prevent multiple rapid reinitializations
    if (this.reinitTimeout) {
      clearTimeout(this.reinitTimeout);
    }

    this.reinitTimeout = setTimeout(() => {
      console.log('[CartDrawerInit] scheduleReinit() executing');
      this.isReinitializing = true;

      requestAnimationFrame(() => {
        setTimeout(() => {
          this.reinitialize();
          this.isReinitializing = false;

          // If another reinit was requested while we were running, do it now
          if (this.pendingReinit) {
            console.log('[CartDrawerInit] Executing pending reinit');
            this.pendingReinit = false;
            this.scheduleReinit();
          }
        }, 50);
      });
    }, 100);
  }

  /**
   * Execute all registered initialization callbacks
   */
  reinitialize() {
    console.log('[CartDrawerInit] Reinitializing, callbacks:', this.initCallbacks.length);
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Check if cart is empty by looking at cart-drawer-items
    const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
    const isEmpty = cartDrawerItems && cartDrawerItems.classList.contains('is-empty');
    
    // Ensure cart-drawer has is-empty class if cart is empty
    if (isEmpty) {
      cartDrawer.classList.add('is-empty');
      console.log('[CartDrawerInit] Cart is empty, added is-empty class to cart-drawer');
    } else {
      cartDrawer.classList.remove('is-empty');
    }

    // Always reinitialize callbacks, even when empty (they handle empty state themselves)
    this.initCallbacks.forEach(({ callback, name }) => {
      try {
        console.log(`[CartDrawerInit] Executing: ${name}`);
        callback();
      } catch (error) {
        console.error(`Failed to reinitialize ${name}:`, error);
      }
    });
    console.log('[CartDrawerInit] Reinit complete');
  }

  /**
   * Initial setup when page loads
   */
  initialSetup() {
    if (this.initialized) return;
    this.reinitialize();
    this.initializeCartItemTracking();
    this.subscribeToCartUpdates();
    this.initialized = true;
  }
}

window.cartDrawerInitManager = new CartDrawerInitManager();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDrawerInitManager.initialSetup();
  });
} else {
  window.cartDrawerInitManager.initialSetup();
}
